import { noticeLayerUnits, lockedNoticeIds } from './noticeLayerGroups';
import type { NoticeLayer } from '@cornerstone/shared';

/** Only paste snapshots created by this editor tab; never interpret arbitrary clipboard JSON as layers. */
let clipboard: { token: string; layers: NoticeLayer[]; offset: number } | null = null;
export function copyNoticeLayers(layers: NoticeLayer[]): string {
  const token = `bricklit-slide-selection:${crypto.randomUUID()}`;
  clipboard = { token, layers: structuredClone(layers), offset: 0 };
  return token;
}
export function readNoticeClipboard(token: string, inPlace: boolean): NoticeLayer[] | null {
  if (!clipboard || token !== clipboard.token) return null;
  if (!inPlace) clipboard.offset += 16;
  const offset = inPlace ? 0 : clipboard.offset;
  return structuredClone(clipboard.layers).map(layer => ({ ...layer, x: layer.x + offset, y: layer.y + offset }));
}
export function cloneNoticeLayers(layers: NoticeLayer[], maxZ: number, offset = 0): NoticeLayer[] {
  const groups=new Map<string,string>();
  for (const layer of layers) if (layer.editorGroup&&!groups.has(layer.editorGroup.id)) groups.set(layer.editorGroup.id,`group-${crypto.randomUUID()}`);
  return structuredClone(layers).sort((a, b) => a.z - b.z).map((layer, index) => ({
    ...layer, ...(layer.editorGroup?{editorGroup:{...layer.editorGroup,id:groups.get(layer.editorGroup.id)!}}:{}), id: `layer-${crypto.randomUUID()}`, x: layer.x + offset, y: layer.y + offset, z: maxZ + index + 1, locked: false,
  }));
}

/** Keep group order; locked layers cannot be selected for a stack move. */
export function orderNoticeLayers(layers: NoticeLayer[], ids: string[], direction: 'back' | 'front', edge: boolean): NoticeLayer[] {
  const locked=lockedNoticeIds(layers);
  const chosen=layers.filter(layer=>ids.includes(layer.id));
  if(!chosen.length||chosen.some(layer=>locked.has(layer.id)))return layers;
  const groupId=chosen[0]?.editorGroup?.id;
  const partial=!!groupId&&chosen.every(layer=>layer.editorGroup?.id===groupId)&&layers.some(layer=>layer.editorGroup?.id===groupId&&!ids.includes(layer.id));
  const units=partial?layers.filter(layer=>layer.editorGroup?.id===groupId).sort((a,b)=>a.z-b.z).map(layer=>({id:layer.id,layers:[layer]})):noticeLayerUnits(layers).reverse();
  const selected=(unit:typeof units[number])=>unit.layers.some(layer=>ids.includes(layer.id));
  let next=[...units];
  if(edge)next=direction==='front'?[...units.filter(unit=>!selected(unit)),...units.filter(selected)]:[...units.filter(selected),...units.filter(unit=>!selected(unit))];
  else if(direction==='front'){for(let i=next.length-2;i>=0;i--)if(selected(next[i]!)&&!selected(next[i+1]!))[next[i],next[i+1]]=[next[i+1]!,next[i]!];}
  else{for(let i=1;i<next.length;i++)if(selected(next[i]!)&&!selected(next[i-1]!))[next[i],next[i-1]]=[next[i-1]!,next[i]!];}
  if(next.every((unit,index)=>unit===units[index]))return layers;
  const ordered=next.flatMap(unit=>[...unit.layers].reverse());
  if(partial){const positions=layers.filter(layer=>layer.editorGroup?.id===groupId).map(layer=>layer.z).sort((a,b)=>a-b);const updates=new Map(ordered.map((layer,index)=>[layer.id,positions[index]!]));return layers.map(layer=>updates.has(layer.id)?{...layer,z:updates.get(layer.id)!}:layer);}
  return ordered.map((layer,z)=>({...layer,z}));
}

export function isNoticeTextInput(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"],[role="menu"]');
}
